// Navigator — a tree of any depth walked ONE LEVEL AT A TIME (ADR-080 §14 —
// the administrative areas; later any deep tree): one back row ("‹ Nabatieh",
// named "Up from Nabatieh" — never a breadcrumb, which grows with the depth),
// the "this level" row (the level's own name with a FIXED second line the
// consumer words — "This area" in a form, "This area and its sub-areas" in a
// filter: a fixed line reads right in every language), then the level's rows.
// Each row has TWO targets: the NAME picks (a box in a filter, a circle in a
// form) and the ARROW goes inside — so the rows are a React Aria GridList
// (rows may hold interactive children; the keyboard moves between rows with
// ↑↓ and reaches the arrow inside a row; verified on the installed build: the
// Autocomplete also filters a GridList, which the search mode rides). Any
// level can be picked; a REFUSED row stays reachable and its arrow live
// (`disabledBehavior="selection"`) — only the pick is refused. A row's second
// line and its inactive word are its description, read with its name. A
// search answers with each match's PATH on its second line, flat, no arrows.
// Desk = phone. Props in, events out:
// the host holds the level, the path and the search (its small source holds
// the whole tree); in pick-one mode a re-press of the chosen row emits an
// EMPTY selection and the host decides. The search box is the family's
// (`PickerSearchBox`), the row classes the family's too.

import { Autocomplete, Button as AriaButton, Checkbox as AriaCheckbox, GridList, GridListItem, Text, type Key, type Selection } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { icons } from './icons.js';
import { PickerSearchBox, pickerRow } from './PickerList.js';

export interface NavigatorRow {
  id: string;
  label: string;
  /** The second line — a search match's PATH ("Lebanon / Mount Lebanon / Aley"), a refusal's reason, a status word. */
  description?: string;
  /** The arrow shows: the row has rows inside. */
  hasChildren?: boolean;
  /** False = refused — greyed, unselectable, still reachable (its arrow still goes inside). */
  pickable?: boolean;
  /** A disabled value — shown marked. */
  inactive?: boolean;
  /** Covered by a picked row above it (a filter keeps the highest pick): drawn ticked, greyed — the row says "Included". */
  included?: boolean;
}

export interface NavigatorProps {
  /** The rows of the current level. */
  rows: readonly NavigatorRow[];
  /** The levels from the root to the OPEN one; empty at the root. The back row shows the last (the open level) and goes up from it. */
  path: readonly { id: string; label: string }[];
  /** The arrow — go inside this row. */
  onEnter: (id: string) => void;
  /** The back row — one level up. */
  onBack: () => void;
  /** The chosen values (a filter: several — boxes; a form: one — a circle). */
  value: readonly string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  /** The "this level" row — the current level's own id and name with its fixed second line; omitted at the root. */
  thisLevel?: { id: string; label: string; line: string };
  /** The search mode: the typed text, the matches with their paths. While text is typed the level's rows are replaced by the matches. */
  search?: { text: string; onTextChange: (text: string) => void; results: readonly NavigatorRow[]; placeholder?: string; autoFocus?: boolean };
  ariaLabel: string;
  /** The host's status line (the spoken count) the list is described by. */
  describedBy?: string;
  /** The one word an EMPTY list says inside itself, when the host knows better than "nothing here" (loading · failed). */
  emptyWord?: string;
  rowSize?: 'default' | 'touch';
  className?: string;
}

function Mark({ multiple, selected }: { multiple: boolean; selected: boolean }) {
  if (multiple) {
    return (
      <span
        aria-hidden
        className={
          'flex size-4 shrink-0 items-center justify-center rounded border ' +
          (selected ? 'border-[var(--color-primary-display)] bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)]' : 'border-[var(--color-border)]')
        }
      >
        {selected && <icons.check size={12} weight="bold" />}
      </span>
    );
  }
  return (
    <span aria-hidden className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-[var(--color-primary-display)]' : 'border-[var(--color-border)]'}`}>
      {selected && <span className="size-2 rounded-full bg-[var(--color-primary-display)]" />}
    </span>
  );
}

export function Navigator({ rows, path, onEnter, onBack, value, onChange, multiple = false, thisLevel, search, ariaLabel, describedBy, emptyWord, rowSize = 'default', className = '' }: NavigatorProps) {
  const { t } = useTranslation('common');
  const touch = rowSize === 'touch';
  const searching = Boolean(search && search.text.trim().length > 0);
  const parent = path[path.length - 1];
  const listed: NavigatorRow[] = searching ? [...search!.results] : [...(thisLevel ? [{ id: thisLevel.id, label: thisLevel.label, description: thisLevel.line, pickable: true }] : []), ...rows];
  const disabledKeys = listed.filter((r) => r.pickable === false).map((r) => r.id);
  const inactiveWord = t('picker.inactive');

  const onSelection = (keys: Selection) => {
    if (keys === 'all') return;
    const next = [...keys].map(String);
    onChange(multiple ? next : next.slice(0, 1));
  };

  const list = (
    <GridList
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      selectionMode={multiple ? 'multiple' : 'single'}
      selectionBehavior="toggle"
      // Escape never clears the chosen (the foundation's default would); it goes up to the host.
      escapeKeyBehavior="none"
      // A refused row is unselectable yet reachable — its arrow still goes inside, its reason is read with it.
      disabledBehavior="selection"
      selectedKeys={new Set(value as Key[])}
      onSelectionChange={onSelection}
      disabledKeys={disabledKeys}
      className="flex w-full flex-col gap-0.5 outline-none"
      // An empty level and an answerless search both say so INSIDE the list (an empty wrapper is a nameless row) —
      // one word: the host's while the level is loading or failed, else nothing here / nothing matches.
      renderEmptyState={() => <p className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">{emptyWord ?? (searching ? t('picker.noResults', { q: search!.text.trim() }) : t('picker.empty'))}</p>}
    >
      {listed.map((row) => (
        <GridListItem key={row.id} id={row.id} textValue={row.label} isDisabled={row.pickable === false} data-refused={row.pickable === false ? '' : undefined} className={pickerRow(touch)}>
          {({ isSelected }) => (
            <>
              {multiple ? (
                <AriaCheckbox slot="selection" aria-label={t('picker.select')} className="flex items-center outline-none">
                  <Mark multiple selected={isSelected || row.included === true} />
                </AriaCheckbox>
              ) : (
                <Mark multiple={false} selected={isSelected} />
              )}
              <span className="min-w-0 flex-1">
                <span className={`block truncate ${row.inactive ? 'text-[var(--color-text-secondary)]' : ''}`}>{row.label}</span>
                {/* The second line and the inactive word are the row's description (read with its name); the word drawn beside the row is decorative. */}
                {(row.description || row.inactive) && (
                  <Text slot="description" className="block truncate text-xs text-[var(--color-text-secondary)]">
                    {row.description}
                    {row.inactive && <span className="sr-only">{row.description ? ` · ${inactiveWord}` : inactiveWord}</span>}
                  </Text>
                )}
              </span>
              {row.inactive && <span aria-hidden className="shrink-0 text-xs text-[var(--color-text-secondary)]">{inactiveWord}</span>}
              {!searching && row.hasChildren && (
                <AriaButton
                  aria-label={t('picker.open', { name: row.label })}
                  onPress={() => onEnter(row.id)}
                  className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-[var(--color-text-secondary)] outline-none focus-ring-inset data-[hovered]:bg-[var(--color-bg-hover)] data-[hovered]:text-[var(--color-text-primary)]"
                >
                  <icons.caretRight aria-hidden size={16} className="rtl:-scale-x-100" />
                </AriaButton>
              )}
            </>
          )}
        </GridListItem>
      ))}
    </GridList>
  );

  const searchBox = search ? <PickerSearchBox value={search.text} onChange={search.onTextChange} placeholder={search.placeholder} focusAtOpen={search.autoFocus} touch={touch} /> : null;

  return (
    <div className={`flex min-h-0 flex-col ${className}`}>
      {/* The back row — one level up, never a breadcrumb; named by its purpose ("Up from Nabatieh" — the visible text inside the name). Hidden while a search answers across every level. */}
      {!searching && parent && (
        <AriaButton
          onPress={onBack}
          aria-label={t('picker.up', { name: parent.label })}
          className={`mb-1 flex w-full items-center gap-1.5 rounded-md px-2 ${touch ? 'min-h-11 text-base' : 'min-h-8 text-sm'} font-medium text-[var(--color-text-secondary)] outline-none focus-ring-inset data-[hovered]:text-[var(--color-text-primary)] data-[hovered]:bg-[var(--color-bg-hover)]`}
        >
          <icons.caretLeft aria-hidden size={14} className="rtl:-scale-x-100" />
          <span className="truncate">{parent.label}</span>
        </AriaButton>
      )}
      {searchBox ? (
        <Autocomplete>
          {searchBox}
          <div className="min-h-0 flex-1 overflow-y-auto">{list}</div>
        </Autocomplete>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">{list}</div>
      )}
    </div>
  );
}
